import { AlertTriangle, Check, Clock, Loader2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { cn } from '~/utils/cn';

import { CheckpointStatus } from '../../types/experiment.types';

const statusStyles: Record<CheckpointStatus, string> = {
  [CheckpointStatus.PASSED]: 'bg-success-50 text-success border-transparent',
  [CheckpointStatus.IN_PROGRESS]: 'bg-info-50 text-info border-transparent',
  [CheckpointStatus.PENDING]: 'bg-neutral-20 text-neutral-70 border-transparent',
  [CheckpointStatus.WARNING]: 'bg-warning-50 text-warning border-transparent',
  [CheckpointStatus.FAILED]: 'bg-error-50 text-error border-transparent',
};

const statusIcons: Record<CheckpointStatus, typeof Check> = {
  [CheckpointStatus.PASSED]: Check,
  [CheckpointStatus.IN_PROGRESS]: Loader2,
  [CheckpointStatus.PENDING]: Clock,
  [CheckpointStatus.WARNING]: AlertTriangle,
  [CheckpointStatus.FAILED]: XCircle,
};

const statusLabels: Record<CheckpointStatus, string> = {
  [CheckpointStatus.PASSED]: 'detail.status.passed',
  [CheckpointStatus.IN_PROGRESS]: 'detail.status.inProgress',
  [CheckpointStatus.PENDING]: 'detail.status.pending',
  [CheckpointStatus.WARNING]: 'detail.status.warning',
  [CheckpointStatus.FAILED]: 'detail.status.failed',
};

interface Props {
  status: CheckpointStatus;
}

export function ExperimentCheckpointStatusBadge({ status }: Props) {
  const { t } = useTranslation('experiments');
  const Icon = statusIcons[status];

  if (status === CheckpointStatus.PENDING) return null; // Hide the badge for pending checkpoints, as per design spec.

  return (
    <Badge
      variant="outline"
      className={cn('gap-1.5 rounded-sm px-2.5 py-1 font-medium', statusStyles[status])}
    >
      <Icon
        className={cn('h-3.5 w-3.5', status === CheckpointStatus.IN_PROGRESS && 'animate-spin')}
        aria-hidden="true"
      />
      {t(statusLabels[status])}
    </Badge>
  );
}
